import { Link } from 'react-router-dom';

import type { Regua } from '@/api/regua';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { acervoQueVence, diaEMes, hojeLocal, plural, proximoComPrazo, rotuloCurto, somarDias } from './reguaDias';

/** "amanhã, 20 de outubro" ou "sex, 30 de outubro" — "sex" sozinho seria ambíguo em duas semanas. */
function quando(iso: string, hoje: string): string {
  return `${rotuloCurto(iso, hoje)}, ${diaEMes(iso)}`;
}

/** "o processo" / "os 3 processos". */
function osProcessos(n: number): string {
  return n === 1 ? 'o processo' : `os ${n} processos`;
}

/**
 * O topo do painel (Bloco F, no desenho do Rubens): o que vence hoje é o título
 * da página, e o botão abre esses processos no acervo. O título conta prazos
 * (intimações) e o botão, processos — as duas unidades à vista, porque um
 * processo pode ter duas intimações no mesmo dia. Sem prazo hoje, diz o
 * próximo: a tela nunca fica muda. Os vencidos da semana numa linha discreta
 * (sem a barra de KPI, era o único lugar do passivo).
 */
export function PrazosDeHoje({ regua, loading }: { regua: Regua | undefined; loading: boolean }) {
  if (loading) return <Skeleton className="h-20 w-full rounded-lg" />;
  if (!regua) return null;

  const { hoje, hoje_prazos: prazos, hoje_processos: processos } = regua;
  const estimados = regua.dias[0]?.estimados ?? 0;
  const depois = regua.dias.slice(1).reduce((soma, d) => soma + d.prazos, 0);
  const proximo = prazos === 0 ? proximoComPrazo(regua.dias) : null;
  const ontem = somarDias(hoje, -1);

  const titulo =
    prazos === 0
      ? 'Nada vence hoje.'
      : `Hoje ${prazos === 1 ? 'vence' : 'vencem'} ${plural(prazos, 'prazo', 'prazos')}.`;
  const subtitulo = proximo
    ? `Próximo: ${quando(proximo.data, hoje)} — ${plural(proximo.prazos, 'prazo', 'prazos')}.`
    : prazos === 0
      ? 'Nenhum prazo nas próximas duas semanas.'
      : depois === 0
        ? 'Nada mais vence nas próximas duas semanas.'
        : `Nas próximas duas semanas ${depois === 1 ? 'vence' : 'vencem'} mais ${depois}.`;

  return (
    <section aria-labelledby="prazos-de-hoje" className="space-y-2">
      {/* Offline, a régua é a última que veio: "hoje" é o dia dela, não o de agora. */}
      {hoje !== hojeLocal() && (
        <p className="text-xs text-muted-foreground">
          Sem atualizar desde {diaEMes(hoje)}: os números são daquele dia.
        </p>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 id="prazos-de-hoje" className="text-3xl font-bold tracking-tight text-primary sm:text-4xl">
            {titulo}
          </h2>
          <p className="mt-1.5 text-base text-foreground/80">
            {subtitulo}
            {estimados > 0 && (
              <span className="text-muted-foreground">
                {' '}
                Hoje, {plural(estimados, 'com vencimento estimado', 'com vencimento estimado')}.
              </span>
            )}
          </p>
        </div>
        {prazos > 0 && (
          <Button asChild size="lg" className="min-h-12 rounded-full px-6 sm:self-center">
            <Link to={acervoQueVence(hoje)}>Abrir {osProcessos(processos)}</Link>
          </Button>
        )}
        {proximo && (
          <Button asChild size="lg" className="min-h-12 rounded-full px-6 sm:self-center">
            <Link to={acervoQueVence(proximo.data)}>Ver {osProcessos(proximo.processos)}</Link>
          </Button>
        )}
      </div>
      {regua.vencidos_prazos > 0 && (
        <p className="text-sm text-muted-foreground">
          {plural(regua.vencidos_prazos, 'prazo venceu', 'prazos venceram')} nos últimos 7 dias, em{' '}
          {plural(regua.vencidos_processos, 'processo', 'processos')} ·{' '}
          <Link
            to={acervoQueVence(regua.vencidos_desde, ontem)}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Revisar
          </Link>
        </p>
      )}
    </section>
  );
}
